import { i18n } from "@/plugins/i18n";
import { DevicePolicyType } from "@/constants/devices";
import {
  INTERVAL_UNIT_TO_REPEAT_INTERVAL,
  REPEAT_INTERVAL_TO_INTERVAL_UNIT,
  RepeatInterval,
} from "@/constants/device-policy";
import type {
  DevicePolicyAdaptedItem,
  DevicePolicyFileIntegrityItem,
  FileIntegrityPolicyPayload,
} from "@/_store/device-policy/device-policy-settings.module";

export const mapFileIntegrityPoliciesToTableItems = (
  policies: DevicePolicyFileIntegrityItem[]
): DevicePolicyAdaptedItem[] => {
  return policies.map((policy) => ({
    tableItemId: `${DevicePolicyType.FILE_INTEGRITY}-${policy.id}`,
    policyType: DevicePolicyType.FILE_INTEGRITY,
    display: {
      os: i18n.global.t(`devicesSettings.devicePolicyTab.osTypes.${policy.os}`),
      repeatEvery: i18n.global.t(
        `devicesSettings.devicePolicyTab.repeatEveryValue.${policy.intervalUnit}`,
        { n: policy.repeatEvery },
        policy.repeatEvery
      ),
    },
    labels: policy.labels,
    monitoredPaths: policy.monitoredPaths,
    raw: policy,
  }));
};

// Applied at fetch time: BE returns repeatInterval + repeatFrequency -> UI intervalUnit + repeatEvery
export const mapFileIntegrityResponseToItems = (
  raw: Array<
    Omit<DevicePolicyFileIntegrityItem, "repeatEvery" | "intervalUnit"> & {
      repeatInterval: RepeatInterval;
      repeatFrequency: number;
    }
  >
): DevicePolicyFileIntegrityItem[] => {
  return raw.map(({ repeatInterval, repeatFrequency, ...rest }) => ({
    ...rest,
    intervalUnit: REPEAT_INTERVAL_TO_INTERVAL_UNIT[repeatInterval],
    repeatEvery: repeatFrequency,
  }));
};

// Builds the payload field by field rather than spreading: the form model also carries `action`,
// which stays UI-only until the BE accepts it (TODO(EDR-2575)).
export const convertFileIntegrityPolicyForBackend = (
  payload: DevicePolicyFileIntegrityItem
): FileIntegrityPolicyPayload => {
  return {
    os: payload.os,
    repeatInterval: INTERVAL_UNIT_TO_REPEAT_INTERVAL[payload.intervalUnit],
    repeatFrequency: payload.repeatEvery,
    monitoredPaths: payload.monitoredPaths.map((p) => p.trim()).filter((p) => p.length > 0),
    labels: payload.labels,
  };
};
